import { endOfMonth, parseISO, startOfMonth, subMonths } from 'date-fns'
import { InsightsHeadcountFragment, InsightsLeaverFragment } from 'generated/resources/graphql'

import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import formatDecimals from 'modules/insights/lib/formatDecimals'

import { ExportRow, GenerateExport } from './export'

const parseDate = (date: Date) => dateToString(date, 'backend')

const findHeadcount = (headcount: InsightsHeadcountFragment[], date: Date) =>
  headcount.find(({ endDate }) => endDate === parseDate(date))?.headcount

const findLeaver = (leavers: InsightsLeaverFragment[], date: Date) =>
  leavers.find(({ endDate }) => endDate === parseDate(date))?.leavers

const calculateRetentionRate = (turnoverRateData: { headcount: number; leavers: number }) => {
  const { headcount, leavers } = turnoverRateData

  if (headcount === 0) return 0

  return ((headcount - leavers) / headcount) * 100
}

export type RetentionByRanges = {
  value: number
  startDate: string
  endDate: string
}

type Props = {
  headcountEvolutionByMonth: InsightsHeadcountFragment[]
  retentionLeavers: InsightsLeaverFragment[]
  date: Date
}

const calculatePreviousCurrentRetention = ({
  headcountEvolutionByMonth,
  retentionLeavers,
  date,
}: Props) => {
  const beggingOfTheMonth = startOfMonth(date)

  const leaversMonth = endOfMonth(subMonths(beggingOfTheMonth, 1))
  const headcountMonth = endOfMonth(subMonths(leaversMonth, 1))

  const leaversPreviousMonth = endOfMonth(subMonths(beggingOfTheMonth, 2))
  const headcountPreviousMonth = endOfMonth(subMonths(leaversPreviousMonth, 1))

  const currentHeadcount = findHeadcount(headcountEvolutionByMonth, headcountMonth)
  const currentLeaver = findLeaver(retentionLeavers, leaversMonth)

  const previousHeadcount = findHeadcount(headcountEvolutionByMonth, headcountPreviousMonth)
  const previousLeavers = findLeaver(retentionLeavers, leaversPreviousMonth)

  const currentMonth = calculateRetentionRate({
    headcount: currentHeadcount!,
    leavers: currentLeaver!,
  })

  const previoustMonth = calculateRetentionRate({
    headcount: previousHeadcount!,
    leavers: previousLeavers!,
  })

  const diff = formatDecimals(currentMonth) - formatDecimals(previoustMonth)

  return [formatDecimals(currentMonth), formatDecimals(diff)] as const
}

export default class Retention implements GenerateExport {
  private headcountEvolutionByMonth: InsightsHeadcountFragment[]
  private leavers: InsightsLeaverFragment[]

  constructor(
    headcountEvolutionByMonth: InsightsHeadcountFragment[],
    leavers: InsightsLeaverFragment[]
  ) {
    this.headcountEvolutionByMonth = headcountEvolutionByMonth
    this.leavers = leavers
  }

  isEmpty() {
    const headcount = this.headcountEvolutionByMonth.every((record) => record.headcount == 0)
    const leavers = this.leavers.every((record) => record.leavers == 0)

    return [headcount, leavers].every((isEmpty) => isEmpty)
  }

  export() {
    const result = this.generate()

    const data: ExportRow[][] = [
      [
        i18n.t('insights.widgets.retention.title'),
        i18n.t('insights.widgets.shared.from_previous_months'),
      ],
      [formatDecimals(result.value), result.formatted],
    ]

    return {
      title: i18n.t('insights.widgets.retention.title'),
      data,
    }
  }

  generateByRanges(): RetentionByRanges[] {
    return this.headcountEvolutionByMonth
      .slice(3, this.headcountEvolutionByMonth.length - 1)
      .map(({ startDate, endDate }) => {
        const [retentionValue, _] = calculatePreviousCurrentRetention({
          headcountEvolutionByMonth: this.headcountEvolutionByMonth,
          retentionLeavers: this.leavers,
          date: parseISO(startDate),
        })

        const value = retentionValue
        return {
          value,
          startDate,
          endDate,
        }
      })
  }

  generate() {
    const [currentMonth, diff] = calculatePreviousCurrentRetention({
      headcountEvolutionByMonth: this.headcountEvolutionByMonth,
      retentionLeavers: this.leavers,
      // This line of code substracts one month so as to calculate the data of the previous month,
      // due to the fact that retention rate only crunch the data of the previous month,
      date: subMonths(new Date(), 1),
    })

    const formattedDiff = Math.abs(diff)

    return {
      value: currentMonth,
      diff: diff,
      formatted: formattedDiff,
    }
  }
}
